import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../services/auth/auth.service';
import { OAuthClientsService } from '../../../services/oauth/oauth-clients.service';
import { OAuthClient } from '../../../dto/oauth/oauth-client.dto';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';
import { DunaOAuthClientsCreateDialogComponent } from './duna-oauth-clients-create-dialog.component';
import { DunaOAuthClientsEditDialogComponent } from './duna-oauth-clients-edit-dialog.component';
import {
  DunaOAuthClientsSecretDialogComponent,
  DunaOAuthClientsSecretDialogData,
} from './duna-oauth-clients-secret-dialog.component';

@Component({
  selector: 'app-duna-oauth-clients',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule,
  ],
  templateUrl: './duna-oauth-clients.component.html',
  styleUrl: './duna-oauth-clients.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DunaOAuthClientsComponent implements OnInit, OnDestroy {
  allowed = false;
  loading = false;
  errorMessage: string | null = null;
  actionId: number | null = null;

  dataSource = new MatTableDataSource<OAuthClient>([]);
  displayedColumns = [
    'name',
    'client_id',
    'enabled',
    'redirect_uris',
    'has_client_secret',
    'product_key',
    'actions',
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private oauthClients: OAuthClientsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.authService
      .getRoles()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((roles) => {
        const normalized = (roles || []).map((r) => r.toLowerCase());
        this.allowed = normalized.includes('superadmin');
        this.cdr.markForCheck();
        if (this.allowed) {
          this.load();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  redirectSummary(row: OAuthClient): string {
    const uris = row.redirect_uris || [];
    if (uris.length === 0) return '—';
    if (uris.length === 1) return uris[0];
    return `${uris[0]} (+${uris.length - 1})`;
  }

  redirectTooltip(row: OAuthClient): string {
    return (row.redirect_uris || []).join('\n');
  }

  openCreate(): void {
    const ref = this.dialog.open(DunaOAuthClientsCreateDialogComponent, {
      width: '520px',
      disableClose: true,
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) return;
      this.load();
      if (result.client_secret) {
        this.openSecretDialog({
          client_id: result.client.client_id,
          client_secret: result.client_secret,
          title: 'Ny OAuth-klient oprettet',
        });
      } else {
        this.snackBar.open('Klient oprettet.', 'Luk', { duration: 4000 });
      }
    });
  }

  openEdit(row: OAuthClient): void {
    const ref = this.dialog.open(DunaOAuthClientsEditDialogComponent, {
      width: '520px',
      data: { client: row },
    });
    ref.afterClosed().subscribe((updated) => {
      if (updated) {
        this.snackBar.open('Klient opdateret.', 'Luk', { duration: 3000 });
        this.load();
      }
    });
  }

  rotateSecret(row: OAuthClient): void {
    const confirmRef = this.dialog.open(ConfirmResetDialogComponent, {
      data: {
        title: 'Rotér client secret',
        message: `Det nuværende secret for «${row.name}» virker ikke længere efter rotation.`,
        details: [
          'Det nye secret vises kun én gang.',
          'Opdater SIM/BFF-konfiguration med det nye secret.',
        ],
        confirmText: 'Rotér',
        confirmColor: 'warn',
      },
    });
    confirmRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.actionId = row.id;
      this.cdr.markForCheck();
      this.oauthClients
        .rotateSecret(row.id)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.actionId = null;
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: (res) => {
            if (res.client_secret) {
              this.openSecretDialog({
                client_id: row.client_id,
                client_secret: res.client_secret,
                title: 'Nyt client secret',
              });
            }
            this.load();
          },
          error: (err) => {
            this.snackBar.open(
              this.formatApiError(err, 'Kunne ikke rotére secret.'),
              'Luk',
              { duration: 6000 }
            );
          },
        });
    });
  }

  disableClient(row: OAuthClient): void {
    const confirmRef = this.dialog.open(ConfirmResetDialogComponent, {
      data: {
        title: 'Deaktiver OAuth-klient',
        message: `Deaktiver «${row.name}» (${row.client_id})?`,
        details: ['Klienten kan ikke længere bruges til login.'],
        confirmText: 'Deaktiver',
        confirmColor: 'warn',
      },
    });
    confirmRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.actionId = row.id;
      this.cdr.markForCheck();
      this.oauthClients
        .disable(row.id)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.actionId = null;
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: () => {
            this.snackBar.open('Klient deaktiveret.', 'Luk', { duration: 3000 });
            this.load();
          },
          error: (err) => {
            this.snackBar.open(
              this.formatApiError(err, 'Kunne ikke deaktivere klient.'),
              'Luk',
              { duration: 6000 }
            );
          },
        });
    });
  }

  load(): void {
    this.loading = true;
    this.errorMessage = null;
    this.cdr.markForCheck();
    this.oauthClients
      .list()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (clients) => {
          this.dataSource.data = clients;
        },
        error: (err) => {
          this.dataSource.data = [];
          this.errorMessage = this.formatApiError(
            err,
            'Kunne ikke hente OAuth-klienter.'
          );
        },
      });
  }

  private openSecretDialog(data: DunaOAuthClientsSecretDialogData): void {
    this.dialog.open(DunaOAuthClientsSecretDialogComponent, {
      width: '520px',
      disableClose: true,
      data,
    });
  }

  private formatApiError(error: unknown, fallback: string): string {
    const httpErr = error as { error?: unknown };
    if (typeof httpErr?.error === 'string') {
      try {
        const parsed = JSON.parse(httpErr.error);
        if (parsed?.message) return String(parsed.message);
      } catch {
        return httpErr.error;
      }
    }
    if (httpErr?.error && typeof httpErr.error === 'object') {
      const msg = (httpErr.error as { message?: string }).message;
      if (msg) return msg;
    }
    return fallback;
  }
}
